import { useCallback, useEffect, useState } from "react";
import vitalsTrackingService from "@/services/vitals-tracking.service";
import {
  VitalsParameter,
  VitalsReading,
  UpdateReadingDto,
} from "@/types/vitals";

export function useVitalsReadings(
  memberId: string,
  parameter: VitalsParameter
) {
  const [readings, setReadings] = useState<VitalsReading[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!memberId) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await vitalsTrackingService.getReadings(
        memberId,
        parameter
      );
      setReadings(result.data);
      setTotal(result.total);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to fetch readings");
    } finally {
      setIsLoading(false);
    }
  }, [memberId, parameter]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const deleteReading = useCallback(
    async (readingId: string) => {
      await vitalsTrackingService.deleteReading(memberId, readingId);
      await fetch();
    },
    [memberId, fetch]
  );

  const updateReading = useCallback(
    async (readingId: string, dto: UpdateReadingDto) => {
      await vitalsTrackingService.updateReading(memberId, readingId, dto);
      await fetch();
    },
    [memberId, fetch]
  );

  return {
    readings,
    total,
    isLoading,
    error,
    refresh: fetch,
    deleteReading,
    updateReading,
  };
}
